import {
  PLANNED_EXIT_ACTIONS_V2,
  PLANNED_EXIT_QUANTITY_MODES_V2,
  PLANNED_EXIT_TRIGGER_TYPES_V2,
  TRADE_HORIZONS_V2,
} from "./trading.js";
import { PORTFOLIO_CAPITAL_BASIS_V2 } from "./policy.js";
import {
  PORTFOLIO_POLICY_ASSET_CLASSES_V2,
  PORTFOLIO_POLICY_DEFINITION_VERSION_V2,
  PORTFOLIO_POLICY_FEE_ASSUMPTION_STATES_V2,
} from "./policy-definition.js";
import { PORTFOLIO_POLICY_DRAFT_FIELDS_V2, type PortfolioPolicyDraftInputV2 } from "./policy-lifecycle.js";

/** Exact runtime shape guard. Semantic and Decimal checks remain in the deterministic validator. */
export function isPortfolioPolicyDraftInputV2(value: unknown): value is PortfolioPolicyDraftInputV2 {
  if (!exactRecord(value, PORTFOLIO_POLICY_DRAFT_FIELDS_V2)) return false;
  return value.definitionVersion === PORTFOLIO_POLICY_DEFINITION_VERSION_V2
    && nullableString(value.baseCurrency) && nullableEnum(value.capitalBasis, PORTFOLIO_CAPITAL_BASIS_V2)
    && nullableString(value.declaredCapital) && plannedFlows(value.plannedCapitalFlows)
    && decimalFields(value) && nullableBoolean(value.allowFractionalQuantity)
    && horizonPolicy(value.horizonPolicy) && assetPolicy(value.assetAllocationPolicy)
    && exitConstraints(value.exitConstraints) && benchmarkPolicy(value.benchmarkPolicy)
    && sourceCostPolicy(value.sourceCostPolicy);
}

function decimalFields(value: Record<string, unknown>): boolean {
  return ["maxOpenRiskPct", "maxTradeRiskPct", "maxPositionPct", "maxSectorExposurePct",
    "maxCorrelatedExposurePct", "maxPortfolioDrawdownPct", "minimumCashReservePct",
    "minimumRewardToRisk", "slippageAllowancePerUnit"].every((field) => nullableString(value[field]));
}

function plannedFlows(value: unknown): boolean {
  return Array.isArray(value) && value.length <= 50 && value.every((item) => exactRecord(item,
    ["sequence", "direction", "amount", "currency", "plannedFor", "note"])
    && Number.isInteger(item.sequence) && (item.direction === "INFLOW" || item.direction === "OUTFLOW")
    && typeof item.amount === "string" && typeof item.currency === "string" && typeof item.plannedFor === "string"
    && nullableString(item.note));
}

function horizonPolicy(value: unknown): boolean {
  if (value === null) return true;
  if (!exactRecord(value, ["defaultHorizon", "allocations"]) || !TRADE_HORIZONS_V2.includes(value.defaultHorizon as never)
    || !Array.isArray(value.allocations)) return false;
  return value.allocations.every((item) => exactRecord(item, ["horizon", "enabled", "targetAllocationPct"])
    && TRADE_HORIZONS_V2.includes(item.horizon as never) && typeof item.enabled === "boolean"
    && typeof item.targetAllocationPct === "string");
}

function assetPolicy(value: unknown): boolean {
  if (value === null) return true;
  if (!exactRecord(value, ["bands"]) || !Array.isArray(value.bands)) return false;
  return value.bands.every((item) => exactRecord(item,
    ["assetClass", "enabled", "minimumAllocationPct", "targetAllocationPct", "maximumAllocationPct"])
    && PORTFOLIO_POLICY_ASSET_CLASSES_V2.includes(item.assetClass as never) && typeof item.enabled === "boolean"
    && typeof item.minimumAllocationPct === "string" && typeof item.targetAllocationPct === "string"
    && typeof item.maximumAllocationPct === "string");
}

function exitConstraints(value: unknown): boolean {
  if (value === null) return true;
  if (!exactRecord(value, ["allowedQuantityModes", "allowedTriggerTypes", "allowedPostExecutionActions",
    "maximumPlannedExits", "requirePreEntryExitPlan", "requireInitialStop", "allowStopWideningAfterEntry"])) return false;
  return enumArray(value.allowedQuantityModes, PLANNED_EXIT_QUANTITY_MODES_V2)
    && enumArray(value.allowedTriggerTypes, PLANNED_EXIT_TRIGGER_TYPES_V2)
    && enumArray(value.allowedPostExecutionActions, PLANNED_EXIT_ACTIONS_V2)
    && Number.isInteger(value.maximumPlannedExits) && typeof value.requirePreEntryExitPlan === "boolean"
    && typeof value.requireInitialStop === "boolean" && typeof value.allowStopWideningAfterEntry === "boolean";
}

function benchmarkPolicy(value: unknown): boolean {
  if (value === null) return true;
  if (!exactRecord(value, ["schemaVersion", "primaryBenchmark", "diagnosticBenchmarks", "cashBenchmark",
    "snapshotCadence", "returnBasis", "portfolioMeasurement", "ownerOutcomeMeasurement", "costBasis",
    "benchmarkSelection", "appendOnly"])) return false;
  return typeof value.schemaVersion === "string" && typeof value.primaryBenchmark === "string"
    && Array.isArray(value.diagnosticBenchmarks) && value.diagnosticBenchmarks.every((item) => typeof item === "string")
    && typeof value.cashBenchmark === "string" && typeof value.snapshotCadence === "string"
    && typeof value.returnBasis === "string" && typeof value.portfolioMeasurement === "string"
    && typeof value.ownerOutcomeMeasurement === "string" && typeof value.costBasis === "string"
    && typeof value.benchmarkSelection === "string" && typeof value.appendOnly === "boolean";
}

function sourceCostPolicy(value: unknown): boolean {
  return value === null || (exactRecord(value, ["brokerSourceId", "feeAssumptionState"])
    && nullableString(value.brokerSourceId)
    && PORTFOLIO_POLICY_FEE_ASSUMPTION_STATES_V2.includes(value.feeAssumptionState as never));
}

function exactRecord<T extends readonly string[]>(value: unknown, keys: T): value is Record<T[number], unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function nullableString(value: unknown): value is string | null { return value === null || typeof value === "string"; }
function nullableBoolean(value: unknown): value is boolean | null { return value === null || typeof value === "boolean"; }
function nullableEnum<T extends string>(value: unknown, allowed: readonly T[]): value is T | null {
  return value === null || (typeof value === "string" && allowed.includes(value as T));
}
function enumArray<T extends string>(value: unknown, allowed: readonly T[]): value is T[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string" && allowed.includes(item as T));
}
